import React, { useState } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '@/integrations/supabase/client';
import { usePostHogAnalytics, ANALYTICS_EVENTS } from '@/hooks/usePostHogAnalytics';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Star } from 'lucide-react';
import { toast } from 'sonner';

interface EnhancedFeedbackDialogProps {
  children: React.ReactNode;
  defaultSection?: string;
  defaultType?: string;
}

const EnhancedFeedbackDialog: React.FC<EnhancedFeedbackDialogProps> = ({ 
  children, 
  defaultSection = '',
  defaultType = 'suggestion'
}) => {
  const session = useSession();
  const { trackEvent } = usePostHogAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [feedback, setFeedback] = useState({
    type: defaultType,
    section: defaultSection,
    subsection: '',
    description: '',
    satisfaction: '3'
  });

  const feedbackTypes = [
    { value: 'bug', label: 'Bug Report' },
    { value: 'suggestion', label: 'Feature Suggestion' },
    { value: 'improvement', label: 'Improvement' },
    { value: 'general', label: 'General Feedback' },
    { value: 'complaint', label: 'Issue/Complaint' }
  ];

  const sections = [
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'artifacts', label: 'Career Artifacts' },
    { value: 'assessment', label: 'Assessment' },
    { value: 'chat', label: 'Chat/AI Assistant' },
    { value: 'navigation', label: 'Navigation' },
    { value: 'profile', label: 'Profile' },
    { value: 'general', label: 'General App Experience' }
  ];

  const sendThankYouEmail = async (userEmail: string, userName?: string) => {
    try {
      console.log('Sending thank you email to:', userEmail);
      
      const { data, error } = await supabase.functions.invoke('send-feedback-email', {
        body: {
          userEmail,
          userName,
          feedbackType: feedback.type,
          section: feedback.section,
          description: feedback.description
        }
      });

      if (error) {
        console.error('Error sending thank you email:', error);
        // Don't show error to user - email is not critical
      } else {
        console.log('Thank you email sent successfully:', data);
      }
    } catch (error) {
      console.error('Failed to send thank you email:', error);
      // Don't show error to user - email is not critical
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session?.user) {
      toast.error('Please sign in to submit feedback');
      return;
    }

    if (!feedback.description.trim()) {
      toast.error('Please provide feedback description');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('feedback')
        .insert({
          user_id: session.user.id,
          type: feedback.type,
          section: feedback.section || null,
          subsection: feedback.subsection || null,
          description: feedback.description,
          status: 'new'
        });

      if (error) throw error;

      // Track with PostHog
      trackEvent(ANALYTICS_EVENTS.FEATURE_USED, {
        feature: 'feedback_system',
        action: 'feedback_submitted',
        feedback_type: feedback.type,
        section: feedback.section,
        satisfaction_score: feedback.satisfaction
      });

      // Send thank you email (don't wait for it to complete)
      if (session.user.email) {
        sendThankYouEmail(session.user.email, session.user.user_metadata?.first_name);
      }

      toast.success('Thank you for your feedback!');
      setIsOpen(false);
      
      // Reset form
      setFeedback({
        type: defaultType,
        section: defaultSection,
        subsection: '',
        description: '',
        satisfaction: '3'
      });
      
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Share Your Feedback
          </DialogTitle>
          <DialogDescription>
            Help us improve your experience. Your feedback is valuable to us!
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="satisfaction">How satisfied are you overall? (1-5)</Label>
            <RadioGroup 
              value={feedback.satisfaction} 
              onValueChange={(value) => setFeedback(prev => ({ ...prev, satisfaction: value }))}
              className="flex space-x-4"
            >
              {[1, 2, 3, 4, 5].map((rating) => (
                <div key={rating} className="flex items-center space-x-1">
                  <RadioGroupItem value={rating.toString()} id={`rating-${rating}`} />
                  <Label htmlFor={`rating-${rating}`} className="flex items-center gap-1">
                    <Star className="w-4 h-4" />
                    {rating}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Feedback Type</Label>
            <Select value={feedback.type} onValueChange={(value) => setFeedback(prev => ({ ...prev, type: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select feedback type" />
              </SelectTrigger>
              <SelectContent>
                {feedbackTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="section">Which area does this relate to?</Label>
            <Select value={feedback.section} onValueChange={(value) => setFeedback(prev => ({ ...prev, section: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select section (optional)" />
              </SelectTrigger>
              <SelectContent>
                {sections.map((section) => (
                  <SelectItem key={section.value} value={section.value}>
                    {section.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Your Feedback *</Label>
            <Textarea
              id="description"
              placeholder="Please share your feedback, suggestions, or report any issues..."
              value={feedback.description}
              onChange={(e) => setFeedback(prev => ({ ...prev, description: e.target.value }))}
              className="min-h-[100px]"
              required
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !feedback.description.trim()}>
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedFeedbackDialog;
